import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { request } from '../request';

interface FormSummary {
  id: string;
  name: string;
  description: string | null;
  object_type: string;
  is_published: boolean;
  embed_token: string;
  created_at: number;
  updated_at: number;
}

export function FormsPage() {
  const [forms, setForms] = useState<FormSummary[] | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    const r = await request<{ results: FormSummary[] }>('/2026-05/forms');
    setForms(r.results);
  }

  async function quickCreate() {
    setCreating(true);
    try {
      await request('/2026-05/forms', {
        method: 'POST',
        body: JSON.stringify({
          name: 'Contact us',
          description: 'A starter form — edit me',
          object_type: '0-1',
          fields: [
            { id: 'email', label: 'Email', type: 'email', required: true },
            { id: 'first_name', label: 'First name', type: 'text', required: false },
            { id: 'last_name', label: 'Last name', type: 'text', required: false },
            { id: 'message', label: 'Message', type: 'textarea', required: false },
          ],
          property_mapping: {
            email: 'email',
            first_name: 'first_name',
            last_name: 'last_name',
            message: 'last_form_message',
          },
        }),
      });
      await load();
    } finally {
      setCreating(false);
    }
  }

  async function togglePublish(form: FormSummary) {
    await request(`/2026-05/forms/${form.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_published: !form.is_published }),
    });
    await load();
  }

  return (
    <div className="p-8 max-w-6xl">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">📝 Forms</h1>
          <p className="text-text-muted text-sm">
            Embed forms on your site — submissions auto-create contacts.
          </p>
        </div>
        <button onClick={quickCreate} disabled={creating} className="btn-primary">
          {creating ? 'Creating…' : '+ Quick-start form'}
        </button>
      </header>

      {forms === null ? (
        <div className="card p-12 text-center text-text-dim">Loading…</div>
      ) : forms.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="text-5xl mb-4">📝</div>
          <h2 className="text-lg font-medium mb-2">No forms yet</h2>
          <p className="text-text-muted text-sm mb-4">
            Quick-start a contact-us form and drop the embed code on your site.
          </p>
          <button onClick={quickCreate} className="btn-primary">
            + Create your first form
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {forms.map((f) => (
            <FormCard key={f.id} form={f} onToggle={() => togglePublish(f)} />
          ))}
        </div>
      )}
    </div>
  );
}

function FormCard({ form, onToggle }: { form: FormSummary; onToggle: () => void }) {
  const apiBase =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:8787'
      : 'https://thehub-api.hphcorp.workers.dev';
  const embedSnippet = `<script src="${apiBase}/embed/form/${form.embed_token}.js" async></script>`;

  const [showEmbed, setShowEmbed] = useState(false);

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-medium">{form.name}</h3>
          {form.description && (
            <p className="text-text-muted text-sm">{form.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-xs px-2 py-1 rounded-full ${
              form.is_published
                ? 'bg-success/20 text-success'
                : 'bg-panel-2 text-text-muted'
            }`}
          >
            {form.is_published ? 'Published' : 'Draft'}
          </span>
          <button onClick={onToggle} className="btn">
            {form.is_published ? 'Unpublish' : 'Publish'}
          </button>
          <Link href={`/forms/${form.id}`} className="btn">
            Submissions
          </Link>
        </div>
      </div>
      <button
        onClick={() => setShowEmbed((v) => !v)}
        className="text-xs text-accent hover:underline"
      >
        {showEmbed ? 'Hide embed code' : 'Show embed code'}
      </button>
      {showEmbed && (
        <div className="mt-3 bg-bg p-3 rounded text-xs font-mono break-all border border-border">
          {embedSnippet}
        </div>
      )}
      {!form.is_published && showEmbed && (
        <p className="text-warning text-xs mt-2">
          The form is in draft — publish it before the embed will accept submissions.
        </p>
      )}
    </div>
  );
}
